import React, { useState, useEffect, useContext, useRef } from "react";
import { useDispatch } from "react-redux";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import background from "../assets/uploadBg.webp";
import BackgroundImage from "../components/BackgroundImage";
import Navbar from "../components/Navbar";
import { v4 as uuid } from 'uuid';
import UploadSettings from "../utils/uploadSettings";
import { toast } from "react-toastify";
import ProgressBar from "../components/ProgressBar";
import { Helmet } from "react-helmet";
import { skipToken } from "@reduxjs/toolkit/query/react";
import imageCompression from 'browser-image-compression';
import { Api } from "../store/apis/api";
import { useFetchBatchNamesQuery, useGetProcessingStatusQuery, useUploadBatchMutation } from "../store/apis/api";
// import { useUploadBatchMutation } from "../store/apis/api";

export default function Upload() {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const settings = useContext(UploadSettings);

  // Local UI state
  const [images_json, setImages_json] = useState([]);
  const [showUpload, setShowUpload] = useState("Select EL image folder.")

  const [taskId, setTaskId] = useState(() => sessionStorage.getItem("taskId"));
  const [status, setStatus] = useState(sessionStorage.getItem("status") || "");
  const [progress, setProgress] = useState(Number(sessionStorage.getItem("progress") || 0));
  const [error, setError] = useState('');

  const [namesFromDB, setNamesFromDB] = useState([]);
  const isDisabled = settings['isDisabled'];
  const batchName = settings['batchName'];
  const numRows = settings['numRows'];
  const numCols = settings['numCols']
    
  const intervalRef = useRef(null);
  const fileInput = useRef(null);
    
  // RTK Query hooks
  const { data: batchNamesData, isLoading: batchNamesLoading } = useFetchBatchNamesQuery();
  const batches = batchNamesData?.batches || [];

  // Upload mutation
  const [upload, { isLoading: isUploadLoading }] = useUploadBatchMutation();

  // Polling hook: will poll only when a valid taskId exists
  const pollingTaskId = taskId ? taskId.replace(/[{}]/g, "") : skipToken;

  const {
    data: statusData,
    isFetching: isPolling,
    refetch: refetchStatus,
    isError: statusIsError,
    error: statusError,
  } = useGetProcessingStatusQuery(pollingTaskId, {
    skip: !taskId,
    pollingInterval: taskId ? 2000 : 0,
    // keepUnusedDataFor: 0, // you can tune cache behaviour in your api slice
  });

    
  const links = [
    { name: "Home", link: "/landing" , isActive: true},
    { name: "Upload new batch", link: "/upload", isActive:false },
    { name: "View analysis", link: "/resultsHighLevel", isActive:false },
    { name: "About", link: "/about", isActive: false},
    { name: "Help", link: "/help", isActive: false}
    // { name: "Contact us", link: "/contactUs", isActive:false },
  ];

  const onOpenFileDialog = () => {
    if (fileInput.current) {
      fileInput.current.click();
    }
  };
    
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        resolve(fileReader.result);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  }; 

  // Folder upload
  const handleFolderUpload = async (filenames) => {
    if (!isDisabled) {
      try {
        setImages_json([]);
        const numFiles = filenames.length;

        // Show initial status
        setShowUpload(`Loading 0 of ${numFiles} images...`);

        const convertToWebP = (file) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              const img = new Image();
              img.onload = () => {
                try {
                  const canvas = document.createElement("canvas");
                  canvas.width = img.width;
                  canvas.height = img.height;
                  const ctx = canvas.getContext("2d");
                  ctx.drawImage(img, 0, 0);

                  canvas.toBlob(
                    (blob) => {
                      if (blob) {
                        const webpFile = new File(
                          [blob],
                          file.name.replace(/\.[^/.]+$/, ".webp"),
                          { type: "image/webp" }
                        );
                        resolve(webpFile);
                      } else {
                        reject(new Error("Canvas WebP conversion failed"));
                      }
                    },
                    "image/webp",
                    0.8
                  );
                } catch (err) {
                    reject(err);
                }
              };
              img.onerror = () =>
                reject(new Error(`Failed to load image: ${file.name}`));
                img.src = e.target.result;
            };
            reader.onerror = () =>
              reject(new Error(`FileReader failed: ${file.name}`));
              reader.readAsDataURL(file);
          });
        };

        const validImgs = [];
        let idCounter = 1;

        // ✅ Sequential loop (no race conditions)
        for (let i = 0; i < numFiles; i++) {
          const file = filenames[i];
          try {
            const webpFile = await convertToWebP(file);
            const base64 = await convertToBase64(webpFile);
            validImgs.push({
              id: idCounter++,
              image: base64,
              filename: webpFile.name,
            });

            // Update progress
            setShowUpload(`Loading image ${i + 1} of ${numFiles}...`);
          } catch (err) {
            console.error(`Skipping file ${file.name}:`, err.message);
          }
        }

        // Save all results
        setImages_json(validImgs);

        // Final status
        if (validImgs.length > 0) {
          setShowUpload(`✅ Finished loading ${validImgs.length} of ${numFiles} image(s).`);
        } else {
          setShowUpload("❌ No valid images could be processed.");
          alert("No valid images could be processed. Please check your files.");
        }

        if (fileInput.current) {
          fileInput.current.value = "";
        }
      } catch (error) {
        console.error("Unexpected error converting files:", error);
        alert("An error occurred while processing the images.");
      }
    }
  };

  // --- Restore UI state on mount so form never remains stuck disabled ---
  useEffect(() => {
    if (window.__REACT_SNAP__) return;
    const savedStatus = sessionStorage.getItem("status");
    if (savedStatus === "processing") {
      settings.updateUploadSettings({ type: "isDisabled", value: true });
    } else {
      // If not processing make sure the form is enabled
      settings.updateUploadSettings({ type: "isDisabled", value: false });
    }
  }, []); // run once on mount

  useEffect(() => {
    if (window.__REACT_SNAP__) return;
    if (!statusData && !statusIsError) return;

    if (statusData) {

      const serverStatus = statusData.status ?? "processing";
      const serverProgress = Number(statusData.progress ?? 0);
      const serverError = statusData.error ?? "";

      if (serverStatus === "completed") {
        // Ensure UI shows 100% before any alerts or resets
        setTaskId(null); 
        setStatus("completed");
        setProgress(100);

        // Save in sessionStorage for persistence
        sessionStorage.setItem("taskId", null);
        sessionStorage.setItem("status", "completed");
        sessionStorage.setItem("progress", 100);
        
        // Perform immediate, synchronous cleanup of sessionStorage
        sessionStorage.removeItem('taskId');
        sessionStorage.removeItem('status');
        sessionStorage.removeItem('progress');
        
        // Re-enable form immediately
        settings.updateUploadSettings({ type: "isDisabled", value: false });

        // stop polling
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        // Run finalization with a slight delay so user sees the final state (100%)
        setTimeout(() => {
          alert("Batch processed successfully. You can now view the analysis.");
          resetForm(); 
          resetTaskState();
        }, 250);

        return;
      }

      if (serverStatus === "failed") {
        setStatus("failed");
        setProgress(serverProgress || 0);
        setError(serverError || "Processing failed");

        // stop polling
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        // Perform immediate, synchronous cleanup of sessionStorage
        sessionStorage.removeItem('taskId');
        sessionStorage.removeItem('status');
        sessionStorage.removeItem('progress');

        // Re-enable form immediately
        settings.updateUploadSettings({ type: "isDisabled", value: false });

        // Run finalization with a slight delay so user sees the final state (100%)
        setTimeout(() => {
          alert(`Batch processing failed: ${statusError || "Unknown error"}`);
          resetForm(); 
          resetTaskState();
        }, 250);

        return;
      }

      setStatus(serverStatus);
      setProgress(serverStatus === "processing" ? serverProgress : serverProgress);
      setError(statusError || "");

      // persist to sessionStorage so state survives navigation
      sessionStorage.setItem("status", serverStatus);
      sessionStorage.setItem("progress", serverProgress);
      // taskId is already saved in handleUploadandProcess
    }

    // 404: task record deleted -> finished
    if (statusIsError && statusError) {
        
        // Treat as completed
        setTaskId(null); 
        setStatus('completed'); // Set to completed briefly for UI
        setProgress(100);
        setError("");

         // cleanup
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        
        sessionStorage.removeItem('taskId');
        sessionStorage.removeItem('status');
        sessionStorage.removeItem('progress');

        // Re-enable form
        settings.updateUploadSettings({ type: "isDisabled", value: false });
      
        // Run finalization alert with a short delay
        setTimeout(() => {
            alert("Batch processed successfully. You can now view the analysis.");
            resetForm(); 
            resetTaskState();
        }, 250);
        
        // Stop execution of the rest of the useEffect
        return; 
    }
  }, [statusData, statusIsError, statusError])


  const handleUploadandProcess = async () => {
    const batchName = settings.batchName;
    const numRows = settings.numRows;
    const numCols = settings.numCols;
    const isDisabled = settings.isDisabled;

    if (!batchName) return alert("Please enter a unique batch name");
    if (numRows === 0) return alert("Please enter the number of rows");
    if (numCols === 0) return alert("Please enter the number of columns");
    if (images_json.length === 0) return alert("Please select the folder containing the EL images");

    if (images_json.length > 100) {
      alert("Too many images uploaded. A maximum of 100 images can be uploaded.");
      resetForm();
      return; // 🚫 stop here
    }

    if (batches.find((b) => b === batchName)) {
      alert("Batch name already exists. Please enter a unique batch name.");
      return;
    }

    if (batches.length >= 15) {
      alert("You can only upload 15 batches. Please delete one before uploading a new batch.");
      resetForm();
      return;
    }

    const newBatch = {
      id: uuid(),
      batch_name: batchName,
      rows: numRows,
      columns: numCols,
      images: images_json,
    };

    try {
      const response = await upload(newBatch).unwrap();
      
      if (response.status === "success") {
        // alert(response.message);
        setTaskId(response.task_id);
        setStatus("processing");
        setProgress(0);

        // Save in sessionStorage for persistence
        sessionStorage.setItem("taskId", response.task_id);
        sessionStorage.setItem("status", "processing");
        sessionStorage.setItem("progress", "0");

        settings.updateUploadSettings({ type: "isDisabled", value: true });
      } else {
        alert(response.message);
        settings.updateUploadSettings({ type: "isDisabled", value: false });
      }
    } catch (error) {
      console.error("Error during upload:", error);
      alert("Error: failed to upload batch.");
      settings.updateUploadSettings({ type: "isDisabled", value: false });
    }
  };

  const resetForm = () => {
    settings.updateUploadSettings({ type: "batchName", value: "" });
    settings.updateUploadSettings({ type: "numRows", value: 0 });
    settings.updateUploadSettings({ type: "numCols", value: 0 });
    setImages_json([]);
    setShowUpload("Select EL image folder."); 
  };

  const resetTaskState = () => {
    // These removals are critical
    sessionStorage.removeItem('taskId');
    sessionStorage.removeItem('status');
    sessionStorage.removeItem('progress');

    // These resets are critical
    setTaskId(null);
    setStatus('');
    setProgress(0);
    setError("");
    
    settings.updateUploadSettings({ type: "isDisabled", value: false });
  };

  return (
    <Container>
      <Helmet>
        <title>Upload a New Batch  | SolarAIDD</title>
        <meta name="description" content="Upload batches of PV EL images to detect defects at batch, module, and cell levels using AI-powered defect detection technology."/>
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://solaraidd.csir.co.za/upload" />
      </Helmet>
      <BackgroundImage background={background} />
      {/* <img src="background" alt="" /> */}
      <Navbar links={links} />
        {!batchNamesLoading && (
          <div className="container">
            <div className="form-container flex column a-center j-center">
              <div className="form flex column a-center j-center">
                <h4>Please enter the information below to upload your batch of EL images and start the magic.</h4>
                <div className="content flex column">
                  <label> Batch name
                    <input
                      type="text"
                      placeholder="Batch name"
                      name = "Batch name"
                      disabled={isDisabled && status === "processing"} // Disable only if processing
                      onChange={(e) => settings.updateUploadSettings({type: "batchName", value: e.target.value})}
                      value={batchName}
                    />
                  </label>
                  <label> Number of rows
                    <input
                      type="number"
                      placeholder="Numer of rows"
                      name = "Numer of rows"
                      max={20}
                      min={1}
                      disabled={isDisabled && status === "processing"} // Disable only if processing
                      onChange={(e) => settings.updateUploadSettings({type: "numRows", value: e.target.value})}
                      value={numRows===0 ? '' : numRows}
                    />
                  </label>
                  <label> Number of columns
                    <input
                      type="number"
                      placeholder="Numer of columns"
                      name="Numer of columns"
                      max={20}
                      min={1}
                      disabled={isDisabled && status === "processing"} // Disable only if processing
                      onChange={(e) => settings.updateUploadSettings({type: "numCols", value: e.target.value})}
                      value={numCols===0 ? '' : numCols}
                    />
                  </label>
                  {/* <h4 style={isDisabled ? {textDecorationLine: 'underline'} : {textDecorationLine: 'underline', cursor: 'pointer'}} onClick={onOpenFileDialog}> {showUpload} </h4> */}
                  <label> Select EL image folder
                    <input
                      type="text"
                      placeholder={showUpload || "Select EL image folder"} //"EL Images"
                      name = "EL Images"
                      disabled={isDisabled && status === "processing"} // Disable only if processing
                      onClick={onOpenFileDialog}
                      style = {{ "cursor": "pointer"}}
                      value={showUpload}
                    /> 
                  </label>
                  <input 
                    directory="" 
                    webkitdirectory="" 
                    type="file"  
                    accept={"image/*"}
                    ref={fileInput}
                    hidden={true}
                    disabled={isDisabled && status === "processing"} // Disable only if processing                    
                    onChange={ (e) => {handleFolderUpload(e.target.files)}}
                  />
                  <button 
                    style={isDisabled ? { background: "grey" } : { background: "#E55B41" }} 
                    onClick={handleUploadandProcess} 
                    disabled={status === "processing"} // Disable only if processing
                  >
                  {isUploadLoading ? (
                    <>
                      Uploading...
                    </>
                    ) : status === "processing" ? (
                    <>
                      Processing...
                    </>
                    ) : (
                    "Upload and Process"
                  )}
                </button>
                {/* Progress Bar and Status */}
                {status && (
                  <div>
                    <p>Status: {status}</p>
                    {progress > 0 && <ProgressBar value={progress} max={100} />}
                    {status === "failed" && <p style={{ color: "red" }}>Error: {error}</p>}
                  </div>
                )}
              </div>
            </div>
          </div>        
        </div>
      )}
    </Container>
  );
}


const Container = styled.div`
position: relative;
.container {    
    position: absolute;
    top: 6rem;
    left: 0;
    width: 100vw;
    grid-template-rows: 15vh 85vh;
    .title {
        text-align: center;
    }
    .form-container {
        padding: 2rem;
        gap: 2rem;
        .form {
            padding: 1rem;
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(12px);
            border-radius: 20px;
            box-shadow: 0 8px 30px rgba(0, 0, 0, 0.65);
            // background-color: #000000b0;
            width: 30vw;
            gap: 2rem;
            color: white;
            .content {
                gap: 2rem;
                input {
                    padding: 0.5rem 1rem;
                    width: 27vw;
                    colour: white;
                }
                button {
                    padding: 0.5rem 1rem;
                    background-color: #E55B41;
                    border: none;
                    cursor: pointer;
                    color: white;
                    border-radius: 0.2rem;
                    font-weight: bolder;
                    font-size: 1.05rem;
                }
            }            
        }
    }
}
`;

